import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, DollarSign, Plus, X, ShieldCheck } from 'lucide-react';

const T = {
  bg: '#F8FAFC',
  panel: '#FFFFFF',
  textMain: '#0F172A',
  textMuted: '#64748B',
  border: '#E2E8F0',
  accent: '#7C3AED',
  accentHover: '#6D28D9',
  success: '#10B981',
  danger: '#EF4444',
  shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
};

export default function BillingModal({ isOpen, onClose, onBalanceUpdated }) {
  const [balance, setBalance] = useState(0);
  const [amount, setAmount] = useState(20);
  const [paymentMethod, setPaymentMethod] = useState('fiat'); // fiat, crypto, dgcc
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const fetchBalance = async () => {
    try {
      setFetching(true);
      const res = await fetch('/api/compute/balance', {
        headers: { 'x-user-id': 'demo_user' }
      });
      const data = await res.json();
      if (data.success) {
        setBalance(data.balance_usd);
      }
    } catch (err) {
      console.error('Failed to fetch balance', err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchBalance();
    }
  }, [isOpen]);

  const handleAddFunds = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/compute/add-funds', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': 'demo_user' },
        body: JSON.stringify({ amount })
      });
      const data = await res.json();
      
      if (data.success) {
        setBalance(data.balance_usd);
        if (onBalanceUpdated) onBalanceUpdated(data.balance_usd);
        alert(`Successfully added $${amount} to your account!`);
        onClose();
      } else {
        alert(data.error || 'Failed to add funds.');
      }
    } catch (err) {
      alert('Network error while adding funds.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        onClick={onClose}
      >
        <motion.div 
          initial={{ opacity: 0, y: 20, scale: 0.95 }} 
          animate={{ opacity: 1, y: 0, scale: 1 }} 
          exit={{ opacity: 0, y: 20, scale: 0.95 }} 
          onClick={e => e.stopPropagation()}
          style={{ background: '#fff', width: '100%', maxWidth: '440px', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
        >
          {/* Header */}
          <div style={{ padding: '24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: 40, height: 40, borderRadius: '10px', background: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.accent }}>
                <CreditCard size={20} />
              </div>
              <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: T.textMain }}>Add Compute Credits</h2>
            </div>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: T.textMuted }}>
              <X size={24} />
            </button>
          </div>

          <div style={{ padding: '32px 24px' }}>
            {/* Current Balance */}
            <div style={{ background: T.bg, padding: '16px', borderRadius: '12px', border: `1px solid ${T.border}`, marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: 600, color: T.textMuted }}>Current Balance</span>
              {fetching ? (
                <div style={{ width: 60, height: 24, background: '#E2E8F0', borderRadius: 4 }} className="animate-pulse" />
              ) : (
                <span style={{ fontSize: '20px', fontWeight: 800, color: T.textMain }}>${balance.toFixed(2)}</span>
              )}
            </div>

            {/* Amount Selector */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: T.textMain, marginBottom: '12px' }}>Select Amount</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                {[10, 20, 50, 100, 250, 500].map(val => (
                  <button 
                    key={val}
                    onClick={() => setAmount(val)}
                    style={{
                      padding: '12px 0', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '15px',
                      background: amount === val ? T.accent : '#fff',
                      color: amount === val ? '#fff' : T.textMain,
                      border: `1px solid ${amount === val ? T.accent : T.border}`,
                      transition: 'all 0.2s'
                    }}
                  >
                    ${val}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Amount (Mocked input display) */}
            <div style={{ position: 'relative', marginBottom: '24px' }}>
              <DollarSign size={16} color={T.textMuted} style={{ position: 'absolute', left: 16, top: 14 }} />
              <input 
                type="number" 
                value={amount} 
                onChange={(e) => setAmount(Number(e.target.value))}
                style={{ width: '100%', padding: '12px 16px 12px 40px', borderRadius: '8px', border: `1px solid ${T.border}`, fontSize: '16px', fontWeight: 600, outline: 'none' }}
              />
            </div>

            {/* Payment Method Selector */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: T.textMain, marginBottom: '12px' }}>Payment Method</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div onClick={() => setPaymentMethod('fiat')} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '12px', background: paymentMethod === 'fiat' ? '#F5F3FF' : '#fff', border: `1px solid ${paymentMethod === 'fiat' ? T.accent : T.border}`, cursor: 'pointer', transition: 'all 0.2s' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fff', boxShadow: T.shadow, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: T.textMain }}>Fiat (Stripe)</div>
                  </div>
                  {paymentMethod === 'fiat' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.accent }} />}
                </div>

                <div onClick={() => setPaymentMethod('crypto')} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '12px', background: paymentMethod === 'crypto' ? '#F5F3FF' : '#fff', border: `1px solid ${paymentMethod === 'crypto' ? T.accent : T.border}`, cursor: 'pointer', transition: 'all 0.2s' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fff', boxShadow: T.shadow, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 18V6"/></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: T.textMain }}>Crypto (NowPayments)</div>
                  </div>
                  {paymentMethod === 'crypto' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.accent }} />}
                </div>

                <div onClick={() => setPaymentMethod('dgcc')} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '12px', background: paymentMethod === 'dgcc' ? '#F5F3FF' : '#fff', border: `1px solid ${paymentMethod === 'dgcc' ? T.accent : T.border}`, cursor: 'pointer', transition: 'all 0.2s' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fff', boxShadow: T.shadow, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l10 6.5v7L12 22 2 15.5v-7L12 2z"/><line x1="12" y1="22" x2="12" y2="9"/><polyline points="2 8.5 12 15 22 8.5"/></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: T.textMain }}>DGCC Token (Wallet)</div>
                  </div>
                  {paymentMethod === 'dgcc' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.accent }} />}
                </div>
              </div>
            </div>

            {/* Action Button */}
            <button 
              onClick={handleAddFunds}
              disabled={loading || amount < 5}
              style={{
                width: '100%', padding: '16px', borderRadius: '12px', border: 'none',
                background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                color: '#fff', fontSize: '16px', fontWeight: 700, cursor: 'pointer',
                display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px',
                opacity: (loading || amount < 5) ? 0.7 : 1,
                boxShadow: '0 10px 25px -5px rgba(124, 58, 237, 0.3)'
              }}
            >
              {loading ? 'Processing...' : (
                <>
                  <Plus size={18} /> Add ${amount} to Balance
                </>
              )}
            </button>
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '16px', fontSize: '12px', color: T.textMuted, fontWeight: 500 }}>
              <ShieldCheck size={14} color="#10B981" /> Secure encrypted payment (Demo Mode)
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
